import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckCircle, AlertTriangle, Info } from 'lucide-react'
import ReportProblemIcon from '@/assets/icons/report_problem.svg'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  "flex items-center gap-2 rounded-200 px-2 py-1 shadow-[4px_4px_6px_0px_var(--color-main-elevation-lv03),-4px_-4px_6px_0px_var(--color-main-elevation-lv03)] [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        success: 'bg-main-support-surface-green text-main-support-on-surface-on-green',
        warning: 'bg-main-support-surface-yellow text-main-support-on-surface-on-yellow',
        info: 'bg-main-support-surface-blue text-main-support-on-surface-on-blue',
        error: 'bg-main-support-surface-red text-main-support-on-surface-on-rose',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
)

const iconMap = {
  success: CheckCircle,
  warning: AlertTriangle,
  info: Info,
  error: ReportProblemIcon,
} as const

export interface AlertProps extends React.ComponentProps<'div'>, VariantProps<typeof alertVariants> {
  action?: React.ReactNode
  icon?: React.ReactNode
}

function Alert({ className, variant = 'info', action, icon, children, ...props }: AlertProps) {
  const Icon = iconMap[variant ?? 'info']

  // Todo: Correct children font-family, font-size, leading after confirm with designer
  return (
    <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      <div className="p-2">{icon ?? <Icon />}</div>
      <span className="flex-1 text-ui-body-b2 leading-ui-body-b2 font-medium">{children}</span>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export { Alert }
